import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import {
  CodeBracketIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  TableCellsIcon,
  FunnelIcon,
  ArrowRightIcon,
  LightBulbIcon,
  ChartBarIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

export default function TriggersGuidePage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Entry Criteria Guide</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <CodeBracketIcon className="h-8 w-8 text-blue-600 mr-3" />
            Master Your Entry Criteria Analysis
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            Analyze record-triggered flows organized by object and entry criteria patterns, helping you identify conflicts, optimize performance, and ensure proper coverage.
          </p>
        </div>

        {/* Overview Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Understanding the Entry Criteria View</h2>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-gray-700 mb-4">
              The Entry Criteria page provides a unique field-centric view of all record-triggered flows in your org. It groups flows by object and shows exactly which fields and conditions are used in entry criteria for each flow, making it easy to spot potential conflicts and optimization opportunities.
            </p>

            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-md">
                <h3 className="font-medium text-blue-900 mb-2">What You'll See:</h3>
                <ul className="space-y-1 text-sm text-blue-700">
                  <li>• Flows grouped by Salesforce object</li>
                  <li>• Entry criteria fields and conditions</li>
                  <li>• Performance grades for each flow</li>
                  <li>• Visual conflict indicators</li>
                  <li>• Before/After save timing</li>
                </ul>
              </div>

              <div className="p-4 bg-purple-50 rounded-md">
                <h3 className="font-medium text-purple-900 mb-2">Key Benefits:</h3>
                <ul className="space-y-1 text-sm text-purple-700">
                  <li>• Identify conflicting entry criteria</li>
                  <li>• Find flows without entry criteria</li>
                  <li>• See entry criteria patterns at a glance</li>
                  <li>• Navigate to flow details quickly</li>
                  <li>• Optimize flow performance</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Field-Centric View Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Field-Centric Organization</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <TableCellsIcon className="h-6 w-6 text-blue-600 mt-1" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">How Flows Are Organized</h3>
                  <p className="mt-1 text-gray-600">
                    Flows are grouped by object, then displayed with their entry criteria patterns for easy comparison.
                  </p>
                  
                  <div className="mt-4 space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900">Object Groups</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Each object (Account, Opportunity, etc.) appears as a collapsible section showing the number of record-triggered flows
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900">Entry Criteria Field Display</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Shows which fields are used in entry criteria for each flow with a visual format like "Status, Stage" or "Any field change"
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900">Entry Conditions</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Displays the actual conditions in plain language, such as "Status equals 'Closed Won'"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Understanding Conflicts Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Identifying Entry Criteria Conflicts</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Conflict Detection</h3>
                <p className="mt-1 text-gray-600">
                  FlowDocs automatically detects when multiple flows might conflict:
                </p>
                
                <div className="mt-4 space-y-4">
                  <div className="p-4 bg-red-50 rounded-md border border-red-200">
                    <h4 className="font-medium text-red-900">🔴 Red Warning Indicators</h4>
                    <p className="text-sm text-red-700 mt-1">
                      Shown when multiple flows have:
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-red-700 ml-4">
                      <li>• Same execution timing (Before/After Save)</li>
                      <li>• Same entry criteria fields or "Any field"</li>
                      <li>• Overlapping entry conditions</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-yellow-50 rounded-md border border-yellow-200">
                    <h4 className="font-medium text-yellow-900">⚠️ No Criteria Warning</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Flows without entry criteria run on every record change, potentially causing:
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-yellow-700 ml-4">
                      <li>• Performance issues</li>
                      <li>• Unnecessary processing</li>
                      <li>• Governor limit consumption</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filters Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Filtering Options</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <FunnelIcon className="h-6 w-6 text-purple-600 mt-1" />
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Available Filters</h3>
                <p className="mt-1 text-gray-600 mb-4">
                  Narrow down your view to focus on specific entry criteria patterns:
                </p>

                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900">Object Filter</h4>
                    <p className="text-sm text-gray-600">
                      Select a specific object like Account or Opportunity to see only its flows
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">Execution Timing</h4>
                    <p className="text-sm text-gray-600">
                      Filter by Before Save, After Save, or see all Record-Triggered flows
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">Entry Criteria Field</h4>
                    <p className="text-sm text-gray-600">
                      Find flows with entry criteria using specific fields like Status, Stage, or Amount
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">Search</h4>
                    <p className="text-sm text-gray-600">
                      Search for flows by name or description
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Visual Indicators Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Visual Indicators</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <ChartBarIcon className="h-6 w-6 text-green-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Understanding the Display</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Performance Grades</h4>
                    <div className="mt-2 flex gap-2">
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">A 95%</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">B 82%</span>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">C 68%</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded">D 45%</span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">I</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">Click grades to navigate to flow details</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">Execution Timing</h4>
                    <p className="text-sm text-gray-600">
                      • <strong>Before Save:</strong> Runs before record is saved to database<br/>
                      • <strong>After Save:</strong> Runs after record is committed<br/>
                      • <strong>Operations:</strong> Create/Update/Delete indicators
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">Special Indicators</h4>
                    <ul className="mt-2 space-y-1 text-sm text-gray-600">
                      <li>• <span className="text-red-600 font-medium">Red text:</span> Potential conflicts detected</li>
                      <li>• <span className="text-yellow-600 font-medium">Yellow warning:</span> No entry criteria</li>
                      <li>• <span className="text-gray-500">Gray "—":</span> No specific entry criteria fields</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Entry Criteria Best Practices</h2>
          
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-200">
            <div className="flex items-start">
              <LightBulbIcon className="h-6 w-6 text-yellow-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Optimization Guidelines</h3>
                <ol className="space-y-3 text-gray-700">
                  <li>
                    <strong>1. Avoid Conflicts:</strong>
                    <p className="text-sm mt-1">Ensure flows on the same object have different entry criteria conditions or consolidate logic</p>
                  </li>
                  <li>
                    <strong>2. Use Entry Criteria:</strong>
                    <p className="text-sm mt-1">Always add conditions to prevent flows from running unnecessarily</p>
                  </li>
                  <li>
                    <strong>3. Specific Entry Criteria Fields:</strong>
                    <p className="text-sm mt-1">Use specific fields in entry criteria rather than "any field" when possible</p>
                  </li>
                  <li>
                    <strong>4. Before vs After Save:</strong>
                    <p className="text-sm mt-1">Use Before Save for field updates, After Save for related records</p>
                  </li>
                  <li>
                    <strong>5. Monitor Performance:</strong>
                    <p className="text-sm mt-1">Keep flows with good grades (A-B) for optimal performance</p>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Common Use Cases */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Common Use Cases</h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-3">🔍 Conflict Resolution</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li>1. Look for red warning indicators on any object</li>
                <li>2. Review the conflicting flows' entry criteria patterns</li>
                <li>3. Either combine flows or adjust entry criteria</li>
                <li>4. Test thoroughly after changes</li>
              </ol>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-3">⚡ Performance Optimization</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li>1. Filter by objects with many flows</li>
                <li>2. Look for flows without entry criteria</li>
                <li>3. Check performance grades</li>
                <li>4. Consolidate or optimize low-performing flows</li>
              </ol>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-3">📊 Impact Analysis</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li>1. Select a specific field in the filter</li>
                <li>2. See all flows triggered by that field</li>
                <li>3. Understand impact before making field changes</li>
                <li>4. Document dependencies</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Next Steps</h2>
          
          <div className="grid gap-4">
            <Link
              to={`${basePath}/entry-criteria`}
              className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CodeBracketIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Analyze Your Entry Criteria</h4>
                    <p className="text-sm text-gray-600">Start reviewing your record-triggered flows</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </Link>

            <Link
              to={`${basePath}/flows`}
              className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <BoltIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">View All Flows</h4>
                    <p className="text-sm text-gray-600">See your complete flow inventory</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 flex items-center">
              <ArrowRightIcon className="h-4 w-4 mr-1 rotate-180" />
              Back to Help Center
            </Link>
            <Link to={`${basePath}/entry-criteria`} className="text-blue-600 hover:text-blue-800 flex items-center">
              View Your Entry Criteria
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}